import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * Oct 2026 — The admin app previously had NO error boundary anywhere.
 * Any exception thrown during render unmounted the entire React root,
 * so the screen simply went blank with no message and every value the
 * admin had typed was destroyed. In the Manual Allocate flow that was
 * indistinguishable from "the form collapsed".
 *
 * This boundary does three things:
 *   1. Keeps the failure local — a throw inside one subtree no longer
 *      takes down the whole admin shell.
 *   2. Shows the error and component stack on screen instead of a
 *      blank page, so a real diagnosis is possible from a screenshot.
 *   3. Logs the full error to the console AND retains it on
 *      `window.__lastAdminError` so it can be read after the fact.
 *
 * `label` identifies which subtree failed in the logged output.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    const label = this.props.label || 'admin';
    this.setState({ info });
    // Keep the raw error reachable after the fact for support.
    try {
      window.__lastAdminError = {
        label,
        message: error?.message || String(error),
        stack: error?.stack || '',
        componentStack: info?.componentStack || '',
        at: new Date().toISOString(),
      };
    } catch { /* ignore */ }
    // eslint-disable-next-line no-console
    console.error(`[${label}] render error:`, error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ error: null, info: null });
  };

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        className="m-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        data-testid={`error-boundary-${this.props.label || 'admin'}`}
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="font-bold">Something went wrong on this screen.</div>
            <div className="mt-1 text-xs">
              Please send this message to support — it identifies the exact cause.
            </div>
            <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-white/70 p-3 text-[11px] leading-relaxed text-red-900">
              {error?.message || String(error)}
              {info?.componentStack ? `\n${info.componentStack}` : ''}
            </pre>
            <button
              type="button"
              onClick={this.handleReset}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-red-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-red-700"
              data-testid="error-boundary-retry"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
